import { cn } from '@/lib/cn';
import { useT } from '@/lib/i18n/useT';
import { fmtInt, type PaceRead } from '../engine';
import { useScrollRamp } from '../motion/useScrollRamp';

export interface GoalCounter {
  label: string;
  pace: PaceRead;
  /** Pista debajo cuando no hay proyección que contar. */
  hint: string;
  /** Con `ratio`, el objetivo es un denominador («de {n} ganados»). */
  mode?: 'pace' | 'ratio';
}

/**
 * Los contadores de Track: la cifra, su objetivo, la barra y una marca negra
 * en el punto donde debería estar la barra por la hora que es. Detrás de la
 * marca quiere decir detrás del calendario, no sólo lejos del objetivo.
 */
export function GoalCounters({ counters, className }: { counters: GoalCounter[]; className?: string }) {
  const t = useT();
  const { ref, p: ramp } = useScrollRamp();
  return (
    <div ref={ref} className={cn('grid gap-4 sm:grid-cols-2 lg:grid-cols-3', className)}>
      {counters.map((c) => {
        const p = c.pace;
        const chip =
          p.chip === 'noGoal'
            ? t('dash.pace.noGoal')
            : p.chip === 'met'
              ? t('dash.pace.met')
              : p.chip === 'ahead'
                ? t('dash.pace.ahead', { d: p.delta ?? 0 })
                : p.chip === 'behind'
                  ? t('dash.pace.behind', { d: p.delta ?? 0 })
                  : p.chip === 'ofGoal'
                    ? t('dash.pace.ofGoal', { p: Math.round(p.pct) })
                    : p.chip === 'redeemed'
                      ? t('dash.pace.redeemed', { p: Math.round(p.pct) })
                      : t('dash.pace.noDenominator');
        const goalLabel =
          c.mode === 'ratio'
            ? p.goal
              ? t('dash.pace.ofWon', { n: fmtInt(p.goal) })
              : t('dash.pace.noneWon')
            : p.goal
              ? t('dash.pace.goalLabel', { n: fmtInt(p.goal) })
              : t('dash.pace.noGoalLabel');
        const neutral = p.chip === 'noGoal' || p.chip === 'noDenominator';
        const barBg = neutral
          ? 'var(--color-border-strong)'
          : c.mode === 'ratio'
            ? 'var(--color-accent-ink)'
            : p.good
              ? 'var(--color-xk-lime)'
              : 'var(--color-warn)';
        const hint =
          p.chip === 'ahead' || p.chip === 'behind'
            ? t('dash.pace.tracking', { n: fmtInt(p.projected), left: fmtInt(Math.max(0, (p.goal ?? 0) - p.actual)) })
            : c.hint;
        return (
          <div
            key={c.label}
            className="flex flex-col gap-2.5 rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] p-5 transition-colors hover:border-[var(--color-accent-ink)]"
          >
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[11px] font-bold uppercase tracking-widest text-[var(--color-fg-muted)]">{c.label}</span>
              <span
                className={cn(
                  'rounded-full px-2 py-0.5 font-mono text-[9px] font-bold uppercase tracking-wider',
                  neutral
                    ? 'bg-[var(--color-bg-elevated)] text-[var(--color-fg-muted)]'
                    : p.good
                      ? 'bg-[var(--color-accent-10)] text-[var(--color-accent-ink)]'
                      : 'bg-[color-mix(in_srgb,var(--color-warn)_14%,transparent)] text-[var(--color-warn-ink)]',
                )}
              >
                {chip}
              </span>
            </div>
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-[2.5rem] font-bold leading-none tabular-nums tracking-tight text-[var(--color-fg-primary)]">
                {fmtInt(Math.round(p.actual * ramp))}
              </span>
              <span className="font-mono text-xs text-[var(--color-fg-muted)]">{goalLabel}</span>
            </div>
            <div className="relative h-2.5 overflow-hidden rounded-full bg-[var(--color-bg-elevated)]">
              <div className="absolute inset-y-0 left-0 rounded-full transition-[width] duration-500" style={{ width: `${p.pct * ramp}%`, background: barBg }} />
              {p.expectedPct != null && (
                <span
                  className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-[var(--color-fg-primary)]"
                  style={{ left: `${p.expectedPct}%` }}
                  title={t('dash.pace.mark')}
                />
              )}
            </div>
            <p className="text-xs leading-relaxed text-[var(--color-fg-muted)]">{hint}</p>
          </div>
        );
      })}
    </div>
  );
}
